import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Phone, Clock, Gauge } from 'lucide-react';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { schema } from '../__generated__/schema-lite';
import { fmtDuration } from '../lib/format';
import { BookedCell, CallerCell } from './CallCells';
import { ListPage } from './dashboard/ListPage';
import { StatCard } from './dashboard/StatCard';

type RecentCall = {
  id: string;
  startedAt: string | null;
  createdAt: string;
  callerName: string | null;
  callerPhone: string | null;
  durationSec: number | null;
  booked: boolean | null;
  endedReason: string | null;
};

/** Read-only oversight home. The top cards use the SAME aggregate as the admin
 *  dashboard (the `agentDashboard` procedure — calls / talk time / avg length
 *  over the last 30 days). Below, the latest calls table. */
export function SuperdocHome() {
  const { t, i18n } = useTranslation();
  const crud = useClientQueries(schema);

  const { data: me } = crud.$procs.me.useQuery();
  const name = (me as { name?: string | null } | undefined)?.name;

  // Real metrics — identical source to the admin dashboard.
  const dashQ = crud.$procs.agentDashboard.useQuery();
  const d = dashQ.data as
    | { totalCalls: number; totalCallsCapped?: boolean; totalDurationSec: number; avgDurationSec: number }
    | undefined;

  // ── Recent calls — latest 12, for the table below ──
  const recentQ = crud.call.useFindMany({
    orderBy: { createdAt: 'desc' },
    take: 12,
    select: {
      id: true,
      startedAt: true,
      createdAt: true,
      callerName: true,
      callerPhone: true,
      durationSec: true,
      booked: true,
      endedReason: true,
    },
  });
  const recent = (recentQ.data as RecentCall[] | undefined) ?? [];

  const dtFmt = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  // Left-aligned, width-capped column (max-width 1180px, no auto margins) —
  // mirrors the mockup's `.pages`.
  return (
    <ListPage
      eyebrow={t('home.eyebrow')}
      title={t('home.greeting', { name: name || t('home.there') })}
      subtitle={t('home.sub')}
      size="lg"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
        <StatCard
          icon={<Phone className="h-4 w-4" />}
          label={t('dashboard.cards.calls')}
          value={d ? (d.totalCallsCapped ? `${d.totalCalls}+` : String(d.totalCalls)) : '—'}
          delta={t('home.stats.window')}
          up
        />
        <StatCard
          icon={<Clock className="h-4 w-4" />}
          label={t('dashboard.cards.time')}
          value={d ? fmtDuration(d.totalDurationSec) : '—'}
          delta={t('home.stats.window')}
          up
        />
        <StatCard
          icon={<Gauge className="h-4 w-4" />}
          label={t('dashboard.cards.avg')}
          value={d ? fmtDuration(d.avgDurationSec) : '—'}
          delta={t('home.stats.window')}
          up
        />
      </div>

      {/* Recent calls — latest 12, with a shortcut to the full call log. */}
      <section className="card p-4 sm:p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('home.recentCalls')}</h2>
          <Link to="/calls" className="text-sm font-medium text-primary hover:underline">
            {t('home.viewAll')} →
          </Link>
        </div>

        {recent.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t('table.empty')}</p>
        ) : (
          <div className="max-h-[70dvh] overflow-auto">
            <table className="w-full min-w-max text-sm">
              <thead className="sticky top-0 z-10 bg-card text-left">
                <tr className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                  <th className="whitespace-nowrap px-3 py-2">{t('fields.Call.startedAt')}</th>
                  <th className="whitespace-nowrap px-3 py-2">{t('fields.Call.callerName')}</th>
                  <th className="whitespace-nowrap px-3 py-2">{t('fields.Call.callerPhone')}</th>
                  <th className="whitespace-nowrap px-3 py-2">{t('fields.Call.durationSec')}</th>
                  <th className="whitespace-nowrap px-3 py-2">{t('fields.Call.booked')}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => {
                  const when = c.startedAt ?? c.createdAt;
                  return (
                    <tr key={c.id} className="border-t border-border hover:bg-muted">
                      <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">
                        {when ? dtFmt.format(new Date(when)) : '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        <CallerCell by="name" value={c.callerName} />
                      </td>
                      <td className="px-3 py-2.5">
                        <CallerCell by="phone" value={c.callerPhone} />
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">
                        {c.durationSec == null ? '—' : fmtDuration(c.durationSec)}
                      </td>
                      <td className="px-3 py-2.5">
                        <BookedCell value={c.booked} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </ListPage>
  );
}
